import { TUNABLES } from "../config";
import type { RunState, SeatRole } from "../core";
import { onLanguageChange, phrases } from "../i18n";
import { ROOM_MODES, type RoomMode } from "../net/protocol";
import { byId, make, makeButton } from "./dom";
import { drawMapInto } from "./heroMap";
import { cardText, type LobbyGame, type LobbySource } from "./lobby";
import { MAX_PARTY_CHARS, partyOf } from "./roomCode";

/**
 * The games' cards on the front page (B3.2): each game's ID, its own map drawn from its seed,
 * its mode, its seats or "Starts when you join", its countdown, and Join. A card keeps its element
 * while its game is listed, so only the words change as the clock runs; a game that leaves the
 * list takes its card with it. Since B3.5 the source may change under the cards (the server's
 * list comes, or goes and the local one is back), and a card can carry a line of its own: the
 * table the visitor sits at, or why the room turned them away. K2: a company duel's card (a King
 * and four soldiers a side) asks which seat, with a King and a Soldier button. K4.1: the card the
 * visitor sits at offers Start now under its table; the company card offers it from the start
 * (D-105), one click to sit as its King and ask to start. K6.1: two against two's card
 * (four Kings on two sides) has one Join, Start now from the start and the party field, so friends
 * sit on one side. VR2: the cards stand in groups by mode, under the mode's title, each one's ID
 * a small line under its seats. No Pixi here.
 */
export interface CardNote {
  /** Its line, written in the language of the moment. */
  readonly text: () => string;
  /** The visitor sits at this duel's table. */
  readonly seated: boolean;
  /**
   * K4.1: the room takes an ask to start now: "offer" shows Start now, "asked" shows it pressed.
   * Absent, no button: the visitor sits nowhere, or a Worker before K4.1 takes no ask.
   */
  readonly startNow?: "offer" | "asked";
}

export interface LobbyCards {
  /** A card's own line; undefined takes it away. */
  note(id: string, note: CardNote | undefined): void;
  /**
   * D-108: Play now's way into the online company: the first listed card of the mode that shows
   * Start now is pressed for `via`, in the seat `wants` (G44: a Change seat's pick; a mode
   * without that seat sits its King); false when none does.
   */
  startNow(mode: RoomMode, via: StartVia, wants?: SeatRole): boolean;
  close(): void;
}

/**
 * What a card says of its mode, and the seats its buttons ask for; `startsNow`: the card offers
 * Start now before a seat is taken (D-105: the company against scripts in one click); `party`:
 * the card has the party field (K3), where friends type the word that seats them together.
 */
const MODE_CARDS: {
  readonly [M in RoomMode]: {
    readonly joins: readonly { readonly wants: SeatRole; readonly text: () => string }[];
    readonly startsNow: boolean;
    readonly party: boolean;
  };
} = {
  duel: {
    joins: [{ wants: "king", text: () => phrases().front.cards.join }],
    startsNow: false,
    party: false,
  },
  company: {
    joins: [
      { wants: "king", text: () => phrases().front.cards.king },
      { wants: "soldier", text: () => phrases().front.cards.soldier },
    ],
    startsNow: true,
    party: true,
  },
  allies: {
    joins: [{ wants: "king", text: () => phrases().front.cards.join }],
    startsNow: true,
    party: true,
  },
  war: {
    joins: [{ wants: "king", text: () => phrases().front.cards.join }],
    startsNow: true,
    party: true,
  },
};

/**
 * A Join of a card: the game, the button pressed, the seat it asks for and, from a company
 * card's field (K3), the party word typed there.
 */
export type JoinGame = (
  game: LobbyGame,
  button: HTMLButtonElement,
  wants: SeatRole,
  party?: string,
) => void;

/**
 * K4.1: a card's Start now: the game, the button of the seat a visitor who sits nowhere yet takes
 * (D-105: the King's; G44: Play now's for a Change seat's pick) with that seat, and the party
 * word of its field.
 */
export type StartGame = (
  game: LobbyGame,
  button: HTMLButtonElement,
  wants: SeatRole,
  party?: string,
  via?: StartVia,
) => void;

/**
 * D-108: who waits on a Start now other than its card: Play now, which enters the game when the
 * room starts it (`enter`) and plays offline when no seat comes (`left`).
 */
export interface StartVia {
  enter(start: () => Promise<void>): void;
  left(): void;
}

/** K4.1: the words of the card's Start now, before and after the visitor pressed it. */
function startNowWord(offered: "offer" | "asked"): string {
  const words = phrases().front.cards;
  return offered === "offer" ? words.startNow : words.asked;
}

/** K4.1: Start now as a card shows it: offered, pressed, or not at all. */
function startNowOf(game: LobbyGame, note: CardNote | undefined): "offer" | "asked" | undefined {
  if (note?.startNow !== undefined) {
    return note.startNow;
  }
  return MODE_CARDS[game.mode].startsNow && note?.seated !== true ? "offer" : undefined;
}

/** A card, and its Start now pressed on behalf of `via` (D-108), in the seat `wants` (G44). */
interface Card {
  readonly card: HTMLLIElement;
  readonly press: (via?: StartVia, wants?: SeatRole) => void;
}

function cardFor(
  game: LobbyGame,
  onJoin: JoinGame,
  onStartNow: StartGame,
  party: string | undefined,
): Card {
  const shape = MODE_CARDS[game.mode];
  const card = make("li", { className: "lobby-card" });
  card.dataset.id = game.id;
  card.dataset.mode = game.mode;
  const map = make("canvas");
  map.setAttribute("role", "img");
  const words = make("div", { className: "words" });
  const status = make("div", { className: "status" });
  status.append(make("span", { className: "seats" }), " ", make("span", { className: "clock" }));
  // VR2: the mode heads its group; the code is a small line, for sharing a table.
  words.append(
    status,
    make("div", { className: "id", text: game.id }),
    make("div", { className: "table-note" }),
  );
  // K3: the party field names the party its Join sits with: one word sits friends together.
  const partyField = shape.party ? partyInput(party) : undefined;
  const partyWord = () => (partyField ? partyOf(partyField.value) : party);
  const buttons = shape.joins.map(({ wants, text }) => {
    const join = makeButton({ className: "join", text: text() });
    join.dataset.wants = wants;
    join.addEventListener("click", () => onJoin(game, join, wants, partyWord()));
    return join;
  });
  // K4.1: shown on the card the visitor sits at, by its note; D-105: the company's from the start.
  const startNow = makeButton({ className: "start-now", text: startNowWord("offer") });
  startNow.hidden = !shape.startsNow;
  const seatButton = (wants: SeatRole) => buttons.find((button) => button.dataset.wants === wants);
  const press = (via?: StartVia, wants: SeatRole = "king") => {
    const seat = seatButton(wants) ? wants : "king";
    const button = seatButton(seat);
    if (button) {
      onStartNow(game, button, seat, partyWord(), via);
    }
  };
  startNow.addEventListener("click", () => press());
  if (!partyField) {
    words.append(startNow);
    card.append(map, words, ...buttons);
  } else {
    const joins = make("div", { className: buttons.length === 1 ? "joins single" : "joins" });
    joins.append(...buttons, startNow, ...(partyField ? [partyField] : []));
    card.append(map, words, joins);
  }
  return { card, press };
}

/** VR2: the title over a mode's tables, the one place the list names the mode. */
function modeHead(mode: RoomMode): HTMLLIElement {
  const head = make("li", { className: "lobby-mode" });
  head.dataset.mode = mode;
  head.append(make("h3", { className: "mode" }));
  speakModeHead(head, mode);
  return head;
}

function speakModeHead(head: HTMLLIElement, mode: RoomMode): void {
  const title = head.querySelector(".mode");
  if (title) {
    title.textContent = phrases().front.cards.modes[mode];
  }
}

/**
 * L10N1: the card's words that stand still while its game is listed (its map's name, its Start
 * now's tooltip and its party field), written again when the language changes.
 */
function speakCard(card: HTMLLIElement, game: LobbyGame): void {
  const words = phrases().front.cards;
  card.querySelector("canvas")?.setAttribute("aria-label", words.mapOf[game.mode](game.id));
  const startNow = card.querySelector<HTMLButtonElement>(".start-now");
  if (startNow) {
    startNow.title = MODE_CARDS[game.mode].startsNow ? words.startNowAsKing : words.startNowAtTable;
  }
  const field = card.querySelector<HTMLInputElement>(".party");
  if (field) {
    field.placeholder = words.partyWord;
    field.title = words.partyHint;
    field.setAttribute("aria-label", words.partyWord);
  }
}

/** A Join's own word, unless it is disabled: then the page has it say Seated or Loading. */
function speakJoins(card: HTMLLIElement, game: LobbyGame): void {
  for (const join of card.querySelectorAll<HTMLButtonElement>(".join")) {
    const word = MODE_CARDS[game.mode].joins.find(({ wants }) => wants === join.dataset.wants);
    if (word && !join.disabled && join.textContent !== word.text()) {
      join.textContent = word.text();
    }
  }
}

/** K3: a card's party field, the address's word in it (K6.1: two against two has one). */
function partyInput(party: string | undefined): HTMLInputElement {
  const field = make("input", { className: "party" });
  field.type = "text";
  field.maxLength = MAX_PARTY_CHARS;
  // Its own width is none: it takes the width of the two seats' buttons above it.
  field.size = 1;
  field.autocomplete = "off";
  field.spellcheck = false;
  field.value = party ?? "";
  return field;
}

export function mountLobbyCards(options: {
  readonly source: LobbySource;
  /** The run at tick 0 a game of the mode plays on a seed text: the map a card draws. */
  readonly runFor: (seedText: string, mode: RoomMode) => RunState;
  readonly onJoin: JoinGame;
  /** K4.1: the visitor pressed Start now (D-105: on the company card, seated or not yet). */
  readonly onStartNow: StartGame;
  /**
   * D-108: the game Play now plays online, told when it changes: the first company game of the
   * lobby server's list, undefined while the list is the page's own.
   */
  readonly onPlayable?: (game: LobbyGame | undefined) => void;
  /**
   * D-120: told once, right after the first refresh that drew a settled source's list (the lobby
   * server's first answer, or its first failure): the page may show its maps and cards from then on.
   */
  readonly onSettled?: () => void;
  /**
   * FP1: the wait at the table the visitor sits at, after every refresh: the ms to its start
   * (zero or less once due), null while seated with no start yet, undefined seated nowhere.
   */
  readonly onCountdown?: (remainingMs: number | null | undefined) => void;
  readonly now: () => number;
  /** K2: the address's party word: every Join's, and the company card's field filled with it (K3). */
  readonly party?: string;
}): LobbyCards {
  const section = byId("lobby", HTMLElement);
  const list = byId("lobby-games", HTMLOListElement);
  const title = byId("lobby-title", HTMLElement);
  section.hidden = false;
  const cards = new Map<string, HTMLLIElement>();
  /** VR2: the title over each listed mode's tables. */
  const heads = new Map<RoomMode, HTMLLIElement>();
  /** D-108: each card's Start now, pressed on behalf of Play now. */
  const presses = new Map<string, (via?: StartVia, wants?: SeatRole) => void>();
  const notes = new Map<string, CardNote>();
  /** D-108: the game last told as Play now's online one. */
  let playable: string | undefined;
  let settled = false;

  const refresh = (): void => {
    const now = options.now();
    section.dataset.lobby = options.source.kind;
    if (title.textContent !== options.source.title) {
      title.textContent = options.source.title;
    }
    // VR2: the tables by mode, in the modes' order; a mode's own keep the source's order.
    const games = [...options.source.games(now)].sort(
      (one, other) => ROOM_MODES.indexOf(one.mode) - ROOM_MODES.indexOf(other.mode),
    );
    const online =
      options.source.kind === "server" ? games.find((game) => game.mode === "company") : undefined;
    if (online?.id !== playable) {
      playable = online?.id;
      options.onPlayable?.(online);
    }
    const listed = new Set(games.map((game) => game.id));
    for (const [id, card] of cards) {
      if (!listed.has(id)) {
        card.remove();
        cards.delete(id);
        presses.delete(id);
      }
    }
    const modes = new Set(games.map((game) => game.mode));
    for (const [mode, head] of heads) {
      if (!modes.has(mode)) {
        head.remove();
        heads.delete(mode);
      }
    }
    let place = 0;
    const placeAt = (item: HTMLLIElement): void => {
      if (list.children[place] !== item) {
        list.insertBefore(item, list.children[place] ?? null);
      }
      place += 1;
    };
    games.forEach((game, index) => {
      if (games[index - 1]?.mode !== game.mode) {
        const head = heads.get(game.mode) ?? modeHead(game.mode);
        heads.set(game.mode, head);
        placeAt(head);
      }
      let card = cards.get(game.id);
      if (!card) {
        const made = cardFor(game, options.onJoin, options.onStartNow, options.party);
        card = made.card;
        presses.set(game.id, made.press);
        speakCard(card, game);
        cards.set(game.id, card);
        list.append(card);
        // A canvas has its size only once its card is in the page.
        const map = card.querySelector("canvas");
        if (map) {
          drawMapInto(
            map,
            options.runFor(game.seedText, game.mode),
            TUNABLES.lobby.thumbPaddingPx,
            TUNABLES.lobby.thumbSeamPx,
          );
        }
      }
      placeAt(card);
      const text = cardText(game, now);
      const seats = card.querySelector(".seats");
      const clock = card.querySelector(".clock");
      if (seats && seats.textContent !== text.seats) {
        seats.textContent = text.seats;
      }
      if (clock && clock.textContent !== text.clock) {
        clock.textContent = text.clock;
      }
      speakJoins(card, game);
      const note = card.querySelector(".table-note");
      const said = notes.get(game.id);
      const line = said?.text() ?? "";
      if (note && note.textContent !== line) {
        note.textContent = line;
      }
      card.toggleAttribute("data-seated", said?.seated === true);
      const startNow = card.querySelector<HTMLButtonElement>(".start-now");
      if (startNow) {
        const offered = startNowOf(game, said);
        startNow.hidden = offered === undefined;
        startNow.disabled = offered === "asked";
        const label = startNowWord(offered ?? "offer");
        if (startNow.textContent !== label) {
          startNow.textContent = label;
        }
      }
      // When it starts on this page's clock: two pages compare countdowns by it (B3.5).
      card.dataset.startsAt = game.startsAt === null ? "" : String(Math.round(game.startsAt));
      card.dataset.state =
        game.startsAt === null ? "waiting" : game.startsAt > now ? "counting" : "starting";
    });
    if (!settled && options.source.settled !== false) {
      settled = true;
      options.onSettled?.();
    }
    const [seatedAt, seatedNote] = [...notes].find(([, note]) => note.seated) ?? [];
    const table = games.find((game) => game.id === seatedAt);
    const remaining = table?.startsAt == null ? null : table.startsAt - now;
    // A visitor who asked to start now waits at most `earlyStartMs` once the lobby has the ask;
    // its seat's first clock (the minute's countdown) is not this visitor's wait.
    const asked = seatedNote?.startNow === "asked";
    options.onCountdown?.(
      seatedAt === undefined
        ? undefined
        : remaining !== null && asked && remaining > TUNABLES.lobby.earlyStartMs
          ? null
          : remaining,
    );
  };
  refresh();
  const timer = setInterval(refresh, TUNABLES.lobby.refreshMs);
  const unheard = onLanguageChange(() => {
    for (const [mode, head] of heads) {
      speakModeHead(head, mode);
    }
    const games = options.source.games(options.now());
    for (const game of games) {
      const card = cards.get(game.id);
      if (card) {
        speakCard(card, game);
      }
    }
    refresh();
  });
  return {
    note(id, note) {
      if (note) {
        notes.set(id, note);
      } else {
        notes.delete(id);
      }
      refresh();
    },
    startNow(mode, via, wants) {
      refresh();
      for (const card of list.querySelectorAll<HTMLLIElement>(".lobby-card")) {
        const shown = card.querySelector<HTMLButtonElement>(".start-now")?.hidden === false;
        const press = presses.get(card.dataset.id ?? "");
        if (card.dataset.mode === mode && shown && press) {
          press(via, wants);
          return true;
        }
      }
      return false;
    },
    close() {
      clearInterval(timer);
      unheard();
    },
  };
}
